import type {
  DisplayConfig,
  RowEdit,
  RowEditInput,
  SearchedMatch,
} from "@models/job";
import type { MappingResultKind } from "@models/mappingResult";
import type { MappingResultRow, RowAlternatives } from "@models/mappingResult";
import type { PatchRequestViewModel } from "@models/revision";

export type RowSaveState = "idle" | "saving" | "saved" | "failed";

export interface RowSaveStatus {
  state: RowSaveState;
  error?: string;
}

export interface BulkEditEntry {
  rowIndex: number;
  edit: RowEditInput;
}

export interface BulkEditResult {
  succeeded: number;
  failed: number;
}

export interface UseJobResultsState {
  rows: MappingResultRow[];
  edits: Map<number, RowEdit>;
  saveStates: Map<number, RowSaveStatus>;
  cursor: string | null;
  display: DisplayConfig | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  notReady: boolean;
  /** One row's candidates, once its dropdown has been opened. Keyed by row
   *  index and cached — they are their own endpoint, not part of the row. */
  alternatives: Map<number, RowAlternatives>;
  /** Row indexes whose candidates are currently being fetched on demand. */
  alternativesLoading: Set<number>;
  applyEdit: (rowIndex: number, edit: RowEditInput) => void;
  clearEdit: (rowIndex: number) => void;
  retrySave: (rowIndex: number) => void;
  bulkApplyEdits: (entries: BulkEditEntry[]) => Promise<BulkEditResult>;
  loadMore: () => Promise<void>;
  /** Lazily fetch + cache one row's candidates (on dropdown expand). */
  loadAlternatives: (
    rowIndex: number,
    kind: MappingResultKind,
  ) => Promise<void>;
  refresh: () => Promise<void>;
  addRow: (
    itemMpId: number,
    value: string,
    searched?: {
      itemMpText: string;
      matches: SearchedMatch[];
      /** Search stages that fell back; gates the server-side "not in RMI"
       *  warning (see `SearchResponse.degraded`). */
      degraded?: string[];
    },
  ) => Promise<void>;
  /** Merge one server row locally (e.g. after a revision restore). */
  applyServerRow: (row: MappingResultRow) => void;
}

export const toPatchPayload = (
  edit: RowEditInput,
): PatchRequestViewModel => {
  const payload: PatchRequestViewModel = { reviewStatus: edit.reviewStatus };
  if (edit.chosenRank !== undefined) payload.chosenRank = edit.chosenRank;
  if (edit.text !== undefined) payload.text = edit.text;
  return payload;
};
